import { DtEventUtils } from "../lmv-compat/DtEventUtils";
import { endpoint } from "../net/endpoints";
import { isNodeJS, getGlobal, isMobileDevice } from "../compat";
import { fetchJson } from "../net/fetch";
import { DtFacility } from "./DtFacility";
import * as dte from "./DtEventTypes";
import { DtPropWorker } from "./DtPropWorkerInstance";
import { DtTeam } from "./DtTeam";
import { fixTwinUrn } from "./encoding/urn";
import { v4 as uuidV4 } from "uuid";
import { DtMsgHub } from "./DtMsgSocket";
import { DtModel } from "./DtModelLMV";
import { DtViews } from "./DtViews";
import { DataVizControls } from "./streams/DataVizControls";
import { DtConstants } from "./schema/DtConstants";
import { getLogger } from "../logger/Logger2IndexedDb";
import { getElementCacheWorkerSingleton } from "./worker/elementcache/DtElementCacheWorkerSingleton";

import { av } from "@adsk/lmv";

const NUM_WORKERS = isMobileDevice() ? 2 : 4;

/**
 * High level object for Tandem application.
 *
 * @alias Autodesk.Tandem.DtApp
 */
export class DtApp extends av.EventDispatcher {
  /**
   * @constructor
   *
   * @param {Object} options - Application configuration options.
   */
  constructor(options) {
    super();

    this.global = getGlobal();

    this.sessionId = uuidV4();
    this.sdkVersion = this.global.DT_SDK_VERSION;
    endpoint.HTTP_REQUEST_HEADERS["Session-Id"] = this.sessionId;
    endpoint.HTTP_REQUEST_HEADERS["x-dt-sdk-version"] = this.sdkVersion;

    this.loadContext = endpoint.initLoadContext(options);
    this.loadContext.sessionId = this.sessionId;
    this.loadContext.sdkVersion = this.sdkVersion;

    // "Shared Facilities" are facilities that the current user has only direct access to,
    // i.e. they belong to groups/accounts said user is not a member of. They show up in
    // the Tandem client in the "Shared with me" tab
    this.sharedFacilities = null;

    this.activeTeamUrn = null;

    if (!isNodeJS()) {
      this._onOnlineBound = this._onOnline.bind(this);
      this._onFocusBound = this._onFocus.bind(this);
      this.global.addEventListener('visibilitychange', this._onFocusBound);
      this.global.addEventListener('online', this._onOnlineBound);
      this.global.addEventListener('offline', this._onOnlineBound);
    }

    this.workers = null;

    //Start the main message web socket connection(s)
    this.msgWs = new DtMsgHub(this.loadContext, console.error, this.sessionId, () => this.signalMsgWsReconnected());

    /**
     * Provides access to current facility.
     *
     * @member {DtFacility}
     * @public
     *
     * @alias Autodesk.Tandem.DtApp#currentFacility
     */
    this.currentFacility = null;
    this.facilityCache = {};

    /**
     * Provides access to available views.
     *
     * @type {DtViews}
     * @public
     *
     * @alias Autodesk.Tandem.DtApp#views
     */
    this.views = new DtViews();

    this.previousFacilityUrn = undefined;

    // A logger instance that also persists logs to IndexedDB. The default level (for console logging) is WARNING.
    // Using getLogger means that the logger instance will be shared by all other places in the main thread that
    // get it this way. Only use it for logs that should be persisted across sessions and avoid log spamming.
    this.logger = getLogger(this.sessionId);
    this.supportedRegions = new Set(endpoint.getSupportedRegions());
  }

  getWorker(seqNo = 0) {
    //TODO: Need a way to shut down the worker when the App is shut down
    if (!this.workers) {
      this.workers = [];
    }

    let whichWorker = seqNo % NUM_WORKERS;

    if (!this.workers[whichWorker]) {
      this.workers[whichWorker] = new DtPropWorker(this, whichWorker, this.sessionId, this.userPreferences,
      this.flags ?? {}); // Only defined when not running in NodeJS
    }

    return this.workers[whichWorker];
  }

  subscribeToEvents(target) {

    if (target instanceof DtModel) {
      //Model data changes need to be sent to the appropriate property worker
      //to process instance tree recalculations, etc.
      target.onEvent = (change) => {
        target.asyncPropertyOperation({ change, operation: "HANDLE_MODEL_CHANGE" });
      };
    } else if (target instanceof DtFacility) {
      //Facility and other global changes don't need to be sent to a worker,
      //we handle them directly here in DtApp
      target.onEvent = (change) => {
        target.onFacilityChanged(change);
      };
    } else if (target instanceof DtTeam) {
      target.onEvent = (change) => {
        target.onTeamChanged(change);
      };
    } else {
      console.error('unrecognized event target', target);
    }
    const loadContext = { ...(target.loadContext || this.loadContext) };
    this.msgWs.subscribeForChanges(loadContext, target.urn(), target.onEvent);
  }

  unsubscribeFromEvents(target) {
    const loadContext = { ...(target.loadContext || this.loadContext) };

    this.msgWs.unsubscribeFromChanges(loadContext, target.urn());
    target.onEvent = null;

    if (target instanceof DtModel && target.isDefault()) {
      // As soon as we disconnect, we could be missing IOT update events.
      target.asyncPropertyOperation({ operation: "MARK_IOT_STALE" });
    }
  }

  // called on connectivity change
  async _onOnline() {
    if (isNodeJS()) {
      return;
    }

    const isConnected = navigator.onLine;

    if (isConnected) {
      this.msgWs.reconnect(true);
      return;
    }
    console.log(`Internet connection lost`);
  }

  // called on window focus event
  async _onFocus() {
    if (isNodeJS()) {
      return;
    }
    const isVisible = !document.hidden;

    if (isVisible) {
      // maybe act on visibility change
      return;
    }
  }

  async loadSharedFacilities(forceRefresh = false) {
    return !forceRefresh && this.loadSharedFacilitiesPromise || (this.loadSharedFacilitiesPromise = this._loadSharedFacilities());
  }

  async _loadSharedFacilities() {
    const userResources = await this.loadUserResources();
    const promisesPerRegion = {};
    for (const twin of userResources.twins) {
      if (this.supportedRegions.has(twin.region) && twin.isShared) {
        if (!promisesPerRegion[twin.region]) {
          const ctx = { ...this.loadContext };
          endpoint.switchRegion(ctx, twin.region);
          promisesPerRegion[twin.region] = fetchJson(ctx, "/users/@me/twins");
        }
      };
    }

    const results = await Promise.all(Object.values(promisesPerRegion));

    const twins = results.reduce((acc, res) => {
      return { ...acc, ...res };
    }, {});

    this.sharedFacilities = [];
    for (let twinID in twins) {
      const settings = twins[twinID];
      const facility = new DtFacility(twinID, this, settings, settings.region);
      this.sharedFacilities.push(facility);
      this.#addToFacilitiesCache(facility);
    }

    this.dispatchEvent({
      type: dte.DT_SHARED_FACILITIES_CHANGED_EVENT
    });
  }

  /**
   * Returns facilities for currently active team.
   *
   * @returns {Promise<Array.<DtFacility>>} List of facilities.
   *
   * @alias Autodesk.Tandem.DtApp#getCurrentTeamsFacilities
   */
  async getCurrentTeamsFacilities() {
    const activeTeam = await this.getActiveTeam();
    const currentTeamFacilities = await activeTeam?.getFacilities();
    for (let facility of currentTeamFacilities ?? []) {
      this.#addToFacilitiesCache(facility);
    }
    return currentTeamFacilities;
  }

  /**
   * Returns facilities shared with the current user.
   *
   * @param {boolean} [forceReload] - If true, forces a reload shared facilities.
   * @returns {Promise<Array.<DtFacility>>} List of facilities.
   *
   * @alias Autodesk.Tandem.DtApp#getUsersFacilities
   */
  async getSharedFacilities(forceReload) {
    await this.loadSharedFacilities(forceReload);
    return this.sharedFacilities;
  }

  async getUsersFacilities(forceReload) {
    console.warn('getUsersFacilities is deprecated, use getSharedFacilities instead');
    return await this.getSharedFacilities(forceReload);
  }

  /**
   * Returns facility by its URN.
   *
   * @param {string} urn
   * @returns {Promise<DtFacility>}
   */
  async getFacility(urn) {
    const twinId = fixTwinUrn(urn);

    // by getting facility directly from cache, we ensure it is already loaded and settings are available
    let facility = this.facilityCache[twinId];

    if (!facility) {
      // if this method is called early enough, this.facilityCache might be empty or incomplete.
      // Specifically, this could happen, when a user visits a facility via a deep link.
      // To populate the cache, we request all facilities setting to be fetched as if the user has visited the home page first.
      // There is no risk of requests duplication becuse getting facilities is protected by deduplication logic internally
      await this.getCurrentTeamsFacilities();
      await this.getSharedFacilities();
      facility = this.facilityCache[twinId];
      if (!facility) {
        facility = await this.getUnknownFacility(twinId);
        if (!facility) return undefined;

        this.#addToFacilitiesCache(facility);
      }
    }

    try {
      await facility.load();
      return facility;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  #addToFacilitiesCache(facility) {
    this.facilityCache[facility.urn()] = facility;
  }

  async createFacility(settings, region = DtConstants.TandemRegions.US, skipDefaultModel) {
    const activeTeam = await this.getActiveTeam();
    const newFacility = await activeTeam.createFacility(settings, region, skipDefaultModel);
    this.#addToFacilitiesCache(newFacility);
    await this.loadUserResources(true);
    return newFacility;
  }

  /**
   * Retrieves a facility object from the facility cache using the provided twin ID.
   *
   * @param {string} twinId - The unique identifier (URN) of the twin whose facility is to be retrieved.
   * @returns {DtFacility} The facility object associated with the given twin ID, or undefined if not found.
   */
  findFacility(twinId) {
    return this.facilityCache[fixTwinUrn(twinId)];
  }

  removeCachedFacility(twinId) {
    delete this.facilityCache[fixTwinUrn(twinId)];
  }

  async #unloadFacility(urnOrFacility) {
    let facility = urnOrFacility;
    if (typeof urnOrFacility === 'string') {
      facility = await this.getFacility(urnOrFacility);
    }

    if (!facility) {
      return;
    }

    if (this.currentFacility && this.currentFacility.urn() === facility.urn()) {
      const viewer = this.currentFacility.viewer;
      if (viewer) {
        viewer.dataViz?.dispose();
        viewer.dataViz = null;
      }

      this.currentFacility.unloadModels();
      this.currentFacility = null;
    }
  }

  async deleteFacility(urn) {
    await this.#unloadFacility(urn);
    const activeTeam = await this.getActiveTeam();
    const facilities = await activeTeam.deleteFacility(urn);
    await this.loadUserResources(true);
    return facilities;
  }


  /**
   * Removes the current user from access to a shared facility identified by its URN.
   *
   * @async
   * @param {string} urn - The unique resource name of the facility to remove access from.
   * @returns {Promise<void>} Resolves when the operation is complete.
   */
  async removeSelfFromSharedFacility(urn) {
    const sharedFacilities = await this.getSharedFacilities();
    const facility = sharedFacilities.find((facility) => facility.urn() == urn);
    if (!facility) {
      console.error("facility not found", urn);
      return;
    }
    await this.#unloadFacility(facility);
    await facility.setUserAccess("@me", DtConstants.DtAccessLevel.NONE);
    this.sharedFacilities = this.sharedFacilities.filter((f) => f != facility);
    this.removeCachedFacility(urn);
    this.dispatchEvent({
      type: dte.DT_SHARED_FACILITIES_CHANGED_EVENT
    });
  }


  /**
   * Displays facility in the viewer.
   *
   * @param {DtFacility} facility
   * @param {Set<string>|View} initialViewInfo
   * @param {Autodesk.Viewing.Viewer3D} viewer
   * @param {boolean} [forceReload]
   * @returns {Promise<void>}
   *
   * @alias Autodesk.Tandem.DtApp#displayFacility
   */
  async displayFacility(facility, initialViewInfo, viewer, forceReload = false) {
    if (this.currentFacility && (this.currentFacility.urn() !== facility.urn() || forceReload)) {
      this.previousFacilityUrn = this.currentFacility.urn();
      this.#unloadFacility(this.currentFacility);
    }

    facility.setViewer(viewer);

    if (!this.currentFacility) {
      this.logger.log(`Loading facility ${facility.twinId}`);
      await facility.load(this.previousFacilityUrn && this.previousFacilityUrn !== facility.urn());
      facility.loadModels(initialViewInfo); // fire and forget
      this.currentFacility = facility;

      // Initialize managers after models load queue is populated
      facility.getStreamManager().init(viewer);
      facility.systemsManager.init();

      if (!isNodeJS()) {
        viewer.dataViz = new DataVizControls(facility.getStreamManager(), this);
        viewer.dataViz.init(viewer);
      }
    }

    return this.currentFacility;
  }

  /**
   * Returns team by its ID.
   *
   * @param {string} groupId
   * @param {Object} [options] - Options object
   * @param {boolean} [options.includeBalance] - If true, includes capacity in the response
   * @returns {Promise<DtTeam>}
   *
   * @alias Autodesk.Tandem.DtApp#getTeamById
   */
  getTeamById(groupId, options = {}) {
    const params = options.includeBalance ? '?includeBalance=true' : '';
    return fetchJson(this.loadContext, `/groups/${groupId}${params}`);
  }

  /**
   * Returns list of teams for current user.
   *
   * @returns {Promise<Array.<DtTeam>>}
   *
   * @alias Autodesk.Tandem.DtApp#getTeams
   */
  async getTeams() {
    if (!this._teamsPromise) {
      this._teamsPromise = (async () => {
        const res = await this.loadUserResources();
        this._teams = res.groups.map((group) => new DtTeam(this, group));
        this.getActiveTeam(); // fire and forget, triggers DT_ACTIVE_TEAM_CHANGED_EVENT if necessary
      })();
    }

    await this._teamsPromise;
    return this._teams;
  }

  async _getUserResources() {
    const res = await fetchJson(
      this.loadContext,
      "/users/@me/resources?includeBalance=true"
    );
    const value = { groups: [], twins: [] };

    for (const group of res.groups) {
      value.groups.push(group);
    }

    for (const twin of res.twins) {
      const isShared = !Boolean(twin.grantedViaGroup);
      value.twins.push({ ...twin, isShared });
    }

    return value;
  }

  async getCurrentTeamBalance(filter) {
    const data = await this._getUserResources();
    const group = data.groups.find((g) => g.urn === this.activeTeamUrn);
    if (!group) {
      return 0;
    }
    if (group.accountSettings.type !== 'capacity') {
      throw new Error('Current team is not a capacity team');
    }

    return filter ? group.capacity[filter] : group.capacity;
  }

  /**
   * Fetches user resources (groups and twins).
   *
   * @param {boolean} [forceRefresh=false] - Force a refresh of cached resources
   * @returns {Promise<Object>} An object containing:
   * - {Array} groups - Array of group objects
   * - {Array} twins - Array of twin objects with isShared flag
   *
   * @example
   * // Returns structure like:
   * {
   *   groups: [
   *     { urn: "group1", name: "Team 1", ... }
   *   ],
   *   twins: [
   *     { urn: "twin1", region: "Twin 1", isShared: true, ... }
   *   ]
   * }
   */
  async loadUserResources(forceRefresh = false) {
    return !forceRefresh && this._userResourcesPromise || (this._userResourcesPromise = this._getUserResources());
  }

  /**
   * Determines the active team in a best effort manner.
   *
   * @returns {Promise<DtTeam>}
   *
   * @alias Autodesk.Tandem.DtApp#getActiveTeam
   */
  async getActiveTeam() {
    const teams = await this.getTeams();

    const activeTeam = teams.find((t) => t.urn() === this.activeTeamUrn) ||
    teams.find((t) => t.isOwner()) ||
    teams.find((t) => t.canManage()) ||
    teams[0];

    this.setAsActiveTeam(activeTeam);

    return activeTeam;
  }

  /** Checks all supported regions if the user has access to this twin */
  async getUnknownFacility(twinId) {
    try {
      const region = await this._findFacilityRegion(twinId);

      if (!region) {
        console.warn(`Unknown facility ${twinId} for current user`);
        return;
      }

      if (!this.supportedRegions.has(region)) {
        console.warn(`Unsupported region ${region} for facility ${twinId}`);
        return;
      }

      const ctx = { ...this.loadContext };
      endpoint.switchRegion(ctx, region);

      const settings = await fetchJson(ctx, `/twins/${twinId}`);
      return new DtFacility(twinId, this, settings, settings.Region);
    } catch (_) {

      // best effort failed, either no access, not found, or an error occurred.
    }}

  async _findFacilityRegion(twinId) {
    const userResources = await this.loadUserResources();
    for (const facility of userResources.twins) {
      if (facility.urn === twinId) {
        return facility.region;
      }
    }
  }

  setAsActiveTeam(dtTeam) {
    // Effecting change on the cached version to not cause a refetch.
    const cachedTeam = this._teams.find((t) => t.urn() === this.activeTeamUrn);

    if (!dtTeam) {
      if (cachedTeam?.onEvent) this.unsubscribeFromEvents(cachedTeam);
      this.setActiveTeamUrn(undefined);
      return;
    }

    const hasChanged = cachedTeam?.urn() !== dtTeam.urn();
    if (hasChanged && cachedTeam?.onEvent) {
      this.unsubscribeFromEvents(cachedTeam);
    }
    this.setActiveTeamUrn(dtTeam.urn());
    if (hasChanged || !dtTeam.onEvent) {
      this.subscribeToEvents(dtTeam);
    }
  }

  setActiveTeamUrn(urn) {
    if (this.activeTeamUrn !== urn) {
      this.activeTeamUrn = urn;
      this.dispatchEvent({ type: dte.DT_ACTIVE_TEAM_CHANGED_EVENT, urn });
      return true;
    }
    return false;
  }

  handleChangeNotification(data) {
    const change = data?.change;

    if (!change) {
      console.error("unknown event", data);
      return;
    }

    if (!SILENCED.has(change.type)) {
      console.info('App dispatching event', change.ctype ?? change.type, change.description);
    }

    const facility = this.findFacility(data.twinId);
    if (!facility) {
      console.warn("unknown facility", data.twinId);
      return;
    }

    //Is it a facility-only event?
    if (!change.modelId) {
      // Redispatch stream events coming from the SDK, on the main thread.
      if (change.type === dte.DT_STREAMS_LAST_READINGS_CHANGED_EVENT) {
        facility.getStreamManager().streamEvents.handleReadingsChanged(change);
        facility.eventTarget.dispatchEvent(change);
        return;
      }

      if (change.type === dte.DT_STREAM_EVENTS_DATA_EVENT || change.type === dte.DT_STREAM_ALERTS_CHANGED_EVENT) {
        facility.eventTarget.dispatchEvent(change);
        return;
      }

      //This code path should not be hit
      console.warn("deprecated handling of twin change notification");
      facility.onFacilityChanged(change);
      return;
    }

    const model = facility.getModels()?.find((m) => m.urn() === change.modelId);
    if (!model) {
      console.warn("unknown model", change.modelId);
      return;
    }

    facility.onModelChanged(model, change);
  }

  /**
   * @param {String} eventName
   * @param {Object} [filter]
   * @param {DtConstants.ChangeTypes} [filter.ctype]
   * @param {String} [filter.twinId]
   * @param {String} [filter.modelId]
   * @param {Number} [timeout]
   * @returns a Promise that waits for a specific change event to occur (locally or via remote notification),
   * up to a given timeout. If the wait times out, the Promise is rejected.
   *
   * @ignore
   */
  async waitForEvent(eventName, filter = null, timeout = 0) {

    if (!filter) {
      return DtEventUtils.waitForFilteredEvent(this, eventName, undefined, timeout);
    }

    function filterCb(e) {
      if (filter.ctype) {
        if (e?.change?.ctype !== filter.ctype) {
          return false;
        }
      }
      if (filter.description) {
        // For testing purposes.
        if (e?.change?.description !== filter.description) {
          return false;
        }
      }
      if (filter.isOwn && !e?.change?.isOwn) {
        return false;
      }
      if (filter.modelId) {
        if (e?.model.urn() !== filter.modelId) {
          return false;
        }
      }
      if (filter.twinId) {
        if (e?.facility.urn() !== filter.twinId) {
          return false;
        }
      }
      return true;
    }

    return DtEventUtils.waitForFilteredEvent(this, eventName, filterCb, timeout);
  }

  signalMsgWsReconnected() {
    this.dispatchEvent({
      type: dte.DT_MSG_WS_RECONNECTED
    });
  }

  async triggerReconnectReplay() {
    this.msgWs.replayOnReconnect();
  }

  /**
   * For internal / support use only.
   * Discard the fragments and element data cache for the current facility.
   * @private
   */
  _clearCacheForFacility() {
    if (!this.currentFacility) {
      return;
    }
    const models = this.currentFacility.getModels(true);
    if (!models || models.length === 0) {
      return;
    }

    const modelIds = [];
    for (let model of models) {
      const id = model.urn().slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);
      modelIds.push(id);
    }
    getElementCacheWorkerSingleton().doOperation({
      operation: 'DT_CLEAR_CACHE_FOR_MODELS',
      modelIds
    });
  }

  /**
   * For internal use only.
   * Downloads a zip file with debug data for the current facility.
   * @private
   */
  _downloadCacheDebugData(excludeDbs) {
    const modelIds = [];
    if (!excludeDbs) {
      if (!this.currentFacility) {
        return;
      }

      const models = this.currentFacility.getModels(true);
      if (!models || models.length === 0) {
        return;
      }

      for (let model of models) {
        const id = model.urn().slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);
        modelIds.push(id);
      }
    }

    getElementCacheWorkerSingleton().downloadDebugData(modelIds, excludeDbs);
  }
}

const SILENCED = new Set([
dte.DT_STREAMS_LAST_READINGS_CHANGED_EVENT,
dte.DT_STREAM_EVENTS_DATA_EVENT]
);

DtApp.prototype.constructor = DtApp;